import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Tag, Timeline, Badge, Alert, Space, Divider, Statistic, theme, Switch, Button, message, Popconfirm, Skeleton } from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  ExclamationCircleOutlined, 
  StopOutlined,
  ArrowRightOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { HiMiniSquare3Stack3D } from "react-icons/hi2";
import { PiGearSixFill } from "react-icons/pi";

import { PiPlugsConnectedFill } from "react-icons/pi";
import { HiMiniQueueList } from "react-icons/hi2";
import { getProcessGroupFlow, startProcessGroup, stopProcessGroup, emptyAllQueues, getProcessGroup } from './utils/nifiHelper';
import { getProcessorsWithQueues, getBusyLevel } from './nifiProcessGroupUtils';

const { Title, Text } = Typography;

const NiFiProcessGroup = ({ processGroupId, groupPath = [], onRefresh, userInfo, onStartStop, onEmptyQueues, actionLoading, isRunning }) => {
  const { token } = theme.useToken();
  const [processGroupData, setProcessGroupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllProcessors, setShowAllProcessors] = useState(false);

  const fetchProcessGroupData = async () => {
    if (!processGroupId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const nifiUsername = userInfo?.nifiUsername;
      const nifiPassword = userInfo?.nifiPassword;
      
      if (!nifiUsername || !nifiPassword) {
        throw new Error('NiFi credentials not configured for this organization');
      }
      
      const flowData = await getProcessGroupFlow(nifiUsername, nifiPassword, processGroupId);
      setProcessGroupData(flowData);
    } catch (err) {
      setError(`Failed to fetch process group data: ${err.message}`);
      console.error('Error fetching process group:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProcessGroupData();
  }, [processGroupId]);

  const getProcessorStatusProps = (runStatus) => {
    switch (runStatus?.toLowerCase()) {
      case 'running':
        return { color: 'green', icon: <PlayCircleOutlined /> };
      case 'stopped':
        return { color: 'default', icon: <PauseCircleOutlined /> };
      case 'invalid':
        return { color: 'red', icon: <ExclamationCircleOutlined /> };
      case 'disabled':
        return { color: 'orange', icon: <StopOutlined /> };
      default:
        return { color: 'default', icon: <PauseCircleOutlined /> };
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get all processors with their queue information
  const getAllProcessors = (processors, connections) => {
    return processors.map(processor => {
      // Find connections that have this processor as destination (incoming queues)
      const incomingConnections = connections.filter(conn => 
        conn.component?.destination?.id === processor.id
      );
      
      // Calculate total queued items for this processor
      const totalQueuedItems = incomingConnections.reduce((total, conn) => {
        const queuedCount = parseInt(conn.status?.aggregateSnapshot?.queuedCount || 0);
        return total + queuedCount;
      }, 0);
      
      return {
        processor,
        totalQueuedItems,
        incomingConnections
      };
    });
  };

  const getBusyColor = (busyLevel) => {
    switch (busyLevel) {
      case 'critical': return token.colorError;
      case 'high': return token.colorWarning;
      case 'medium': return token.colorPrimary;
      case 'low': return token.colorWarning;
      default: return token.colorTextSecondary;
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '240px', width: '100%' }}>
        <Card size="small">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 1, width: ['60%'] }} />
            <Skeleton active paragraph={{ rows: 6 }} />
          </Space>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Process Group"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  if (!processGroupData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="secondary">No process group data available</Text>
      </div>
    );
  }

  const flow = processGroupData.processGroupFlow?.flow;
  const processors = flow?.processors || [];
  const connections = flow?.connections || [];
  const breadcrumb = processGroupData.processGroupFlow?.breadcrumb;

  // Get processors based on toggle state
  const processorsWithQueues = getProcessorsWithQueues(processors, connections);
  const allProcessors = getAllProcessors(processors, connections);
  const displayProcessors = showAllProcessors ? allProcessors : processorsWithQueues;

  // Create timeline items for processors
  const timelineItems = displayProcessors.map(({ processor, totalQueuedItems, incomingConnections }) => {
    const status = processor.status?.aggregateSnapshot;
    const runStatus = status?.runStatus || processor.component?.state;
    const statusProps = getProcessorStatusProps(runStatus);
    const busyLevel = getBusyLevel(totalQueuedItems);
    const busyColor = getBusyColor(busyLevel);
    const hasQueue = totalQueuedItems > 0;

    return {
      dot: (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          fontSize: '20px'
        }}>
          {statusProps.icon}
        </div>
      ),
      color: hasQueue ? busyColor : statusProps.color,
      children: (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 8, 
            backgroundColor: 'transparent',
            border: hasQueue ? `1px solid ${busyColor}` : `1px solid ${statusProps.color}`,
            borderBottom: hasQueue ? `3px solid ${busyColor}` : `3px solid ${statusProps.color}`,
            position: 'relative',
            overflow: 'hidden'
          }}
          bodyStyle={{
            background: hasQueue ? `linear-gradient(0deg, ${busyColor}33, transparent)` : 'none'
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <PiGearSixFill style={{ fontSize: '16px' }} />
              <Text strong>{processor.component?.name}</Text>
              <Tag color={statusProps.color}>{runStatus}</Tag>
              {hasQueue && (
                <Badge 
                  count={totalQueuedItems} 
                  style={{ 
                    backgroundColor: busyColor,
                    boxShadow: `0 0 6px ${busyColor}66`
                  }} 
                />
              )}
              {hasQueue && (
                <Tag style={{
                  background: `${busyColor}33`,
                  color: busyColor,
                  border: 'none',
                  fontWeight: token.fontWeightStrong
                }}>
                  {busyLevel.charAt(0).toUpperCase() + busyLevel.slice(1)} Queue
                </Tag>
              )}
              {!hasQueue && (
                <Tag style={{
                  background: `${token.colorSuccess}33`,
                  color: token.colorSuccess,
                  border: 'none',
                  fontWeight: token.fontWeightStrong
                }}>
                  No Queue
                </Tag>
              )}
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {processor.component?.type?.split('.').pop()}
            </Text>
            {hasQueue && (
              <Space>
                <HiMiniQueueList style={{ color: busyColor, fontSize: '14px' }} />
                <Text 
                  type="secondary" 
                  style={{ 
                    fontSize: '12px',
                    color: busyLevel === 'critical' ? busyColor : token.colorTextSecondary
                  }}
                >
                  Total Queued Items: {totalQueuedItems}
                </Text>
              </Space>
            )}
            <Row gutter={16}>
              <Col span={8}>
                <Statistic 
                  title="Input" 
                  value={status?.input || '0 (0 bytes)'} 
                  valueStyle={{ fontSize: '12px' }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Output" 
                  value={status?.output || '0 (0 bytes)'} 
                  valueStyle={{ fontSize: '12px' }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Tasks" 
                  value={status?.tasks || '0'} 
                  valueStyle={{ fontSize: '12px' }}
                />
              </Col>
            </Row>
          </Space>
        </Card>
      )
    };
  });

  const totalQueuedFiles = connections.reduce((total, conn) => 
    total + parseInt(conn.status?.aggregateSnapshot?.queuedCount || 0), 0
  );

  const totalQueuedBytes = connections.reduce((total, conn) => 
    total + parseInt(conn.status?.aggregateSnapshot?.bytesQueued || 0), 0
  );

  const fallbackName = breadcrumb?.breadcrumb?.name || 'Process Group';
  const pathSegments = groupPath?.length > 0 ? groupPath : [fallbackName];
  const rootName = pathSegments[0];
  const subgroupPath = pathSegments.slice(1);

  return (
    <Space direction="vertical" size="medium" style={{ width: '100%' }}>
      {/* Summary Statistics Card */}
      <Card
        title={
          <div>
            <Title level={4} style={{ margin: 0, display: 'block' }}>
              {rootName}
            </Title>
            {subgroupPath.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {subgroupPath.map((name, index) => {
                  const isLast = index === subgroupPath.length - 1;
                  return (
                    <div
                      key={`${name}-${index}`}
                      style={{ paddingLeft: index * 20, marginTop: index === 0 ? 0 : 2 }}
                    >
                      <Text
                        type="secondary"
                        style={{
                          fontSize: isLast ? 15 : 14,
                          lineHeight: '24px',
                          fontWeight: isLast ? 500 : 400,
                          color: isLast ? token.colorText : token.colorTextSecondary,
                        }}
                      >
                        └ {name}
                      </Text>
                    </div>
                  );
                })}
              </div>
            )}
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 6 }}>
              ID: {processGroupId}
            </Text>
          </div>
        }
      >
      <Row gutter={16}>
        <Col span={6}>
          <Statistic
            title="Queued Files"
            value={totalQueuedFiles}
            prefix={<HiMiniSquare3Stack3D style={{ color: '#8c8c8c', fontSize: '24px' }} />}
            valueStyle={{ color: totalQueuedFiles > 0 ? '#fa8c16' : '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Queued Size"
            value={formatBytes(totalQueuedBytes)}
            valueStyle={{ color: totalQueuedBytes > 0 ? '#fa8c16' : '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Processors"
            value={processors.length}
            prefix={<PiGearSixFill style={{ fontSize: '24px' }} />}
            valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Connections"
            value={connections.length}
            prefix={<PiPlugsConnectedFill style={{ color: '#8c8c8c', fontSize: '24px' }} />}
            valueStyle={{ fontSize: '28px', fontWeight: 'bold' }}
          />
        </Col>
      </Row>
      </Card>

      <Divider />

      {/* Processors */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0 }}>
            {showAllProcessors ? `All Processors (${allProcessors.length})` : `Processors with Queues (${processorsWithQueues.length})`}
          </Title>
          <Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>Show all processors</Text>
            <Switch 
              checked={showAllProcessors}
              onChange={setShowAllProcessors}
              size="small"
            />
          </Space>
        </div>
        {timelineItems.length > 0 ? (
          <div style={{ 
            height: '600px', 
            overflowY: 'auto',
            padding: '16px'
          }}>
            <Timeline items={timelineItems} />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">
              {showAllProcessors 
                ? 'No processors found in this process group' 
                : 'No processors with queues containing more than 1 item'
              }
            </Text>
          </div>
        )}
      </div>
    </Space>
  );
};

export default NiFiProcessGroup;
